import React, { useState, useMemo, useRef, useCallback } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery } from "@tanstack/react-query";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  FileText, Download, Loader2, Building2, Calendar, TrendingUp,
  TrendingDown, Wallet, Wheat, Package, Printer, Landmark, FileCheck, Truck, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import FiltrosAvancados from "@/components/financeiro/FiltrosAvancados";
import ExportacaoRelatorios from "@/components/financeiro/ExportacaoRelatorios";
import RelatorioBancario from "../components/relatorios/RelatorioBancario";
import RelatorioFluxoCaixaDetalhado from "../components/relatorios/RelatorioFluxoCaixaDetalhado";
import RelatorioRentabilidadeSafra from "../components/relatorios/RelatorioRentabilidadeSafra";
import RelatorioDesempenhoAtivos from "../components/relatorios/RelatorioDesempenhoAtivos";

export default function Relatorios() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const [tipoRelatorio, setTipoRelatorio] = useState("dre");
  const [periodo, setPeriodo] = useState("12");
  const reportRef = useRef(null);
  const [filtros, setFiltros] = useState({
    busca: "",
    dataInicio: "",
    dataFim: "",
    categoria: "todas",
    tipo: "todos",
    status: "todos",
    valorMin: "",
    valorMax: ""
  });

  // --- REPORT RPCS ---
  const { data: dreData, isLoading: loadingDre } = useQuery({
    queryKey: ['dre_report', propriedadeId, periodo],
    queryFn: async () => {
      if (!propriedadeId) return null;
      const { data, error } = await agrogestao.client.rpc('get_dre_report', {
        p_propriedade_id: propriedadeId,
        p_meses: parseInt(periodo)
      });
      if (error) throw error;

      const receitas = (data.receitas || []).map(r => [r.categoria, r.valor]);
      const despesas = (data.despesas || []).map(d => [d.categoria, d.valor]);

      return {
        ...data,
        receitas,
        despesas,
        resultado: data.resultado
      };
    },
    enabled: !!propriedadeId && ['dre', 'bancario', 'viabilidade'].includes(tipoRelatorio)
  });

  const { data: fluxoCaixaData = [], isLoading: loadingFluxo } = useQuery({
    queryKey: ['fluxo_caixa_report', propriedadeId, periodo],
    queryFn: async () => {
      if (!propriedadeId) return [];
      const { data, error } = await agrogestao.client.rpc('get_fluxo_caixa', {
        p_propriedade_id: propriedadeId,
        p_meses: parseInt(periodo)
      });
      if (error) throw error;
      return (data || []).map(m => ({
        mes: m.mes_label,
        receitas: m.receitas,
        despesas: m.despesas,
        saldo: m.saldo
      }));
    },
    enabled: !!propriedadeId && ['fluxo', 'bancario'].includes(tipoRelatorio)
  });

  // --- ENTITY QUERIES (Reduced Load - Restored for other tabs) ---
  const { data: propriedade } = useQuery({
    queryKey: ['propriedade', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Propriedade.filter({ id: propriedadeId }).then(r => r[0])
      : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: insumos = [] } = useQuery({
    queryKey: ['insumos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Insumo.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: estoquesGrao = [] } = useQuery({
    queryKey: ['estoques_grao', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.EstoqueBalanco.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: financiamentos = [] } = useQuery({
    queryKey: ['financiamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Financiamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasPagar = [] } = useQuery({
    queryKey: ['contas_pagar', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaPagar.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasReceber = [] } = useQuery({
    queryKey: ['contas_receber', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaReceber.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: custosAlocados = [] } = useQuery({
    queryKey: ['custos_alocados', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.CustoAlocado.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: ativos = [] } = useQuery({
    queryKey: ['ativos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.AtivoImobilizado.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: manutencoes = [] } = useQuery({
    queryKey: ['manutencoes', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ManutencaoAtivo.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: lancContabilidade = [] } = useQuery({
    queryKey: ['vw_lancamentos_contabilidade', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.client.from('vw_lancamentos_contabilidade').select('*').eq('propriedade_id', propriedadeId).then(r => r.data || [])
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [] } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId }, '-data', 1000)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  // --- FILTERING & UTILS ---
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const categoriasTodas = useMemo(() => {
    const cats = [
      ...lancamentos.map(l => l.categoria),
      ...contasPagar.map(c => c.categoria),
      ...contasReceber.map(c => c.categoria)
    ].filter(Boolean);
    return [...new Set(cats)].sort();
  }, [lancamentos, contasPagar, contasReceber]);

  const lancamentosFiltrados = useMemo(() => {
    const aplicaBusca = (l) => {
      if (!filtros.busca) return true;
      const b = filtros.busca.toLowerCase();
      return (
        (l.descricao || "").toLowerCase().includes(b) ||
        (l.categoria || "").toLowerCase().includes(b) ||
        (l.fornecedor_cliente || "").toLowerCase().includes(b)
      );
    };
    const dentroPeriodo = (l) => {
      if (filtros.dataInicio && l.data < filtros.dataInicio) return false;
      if (filtros.dataFim && l.data > filtros.dataFim) return false;
      if (!filtros.dataInicio && !filtros.dataFim) {
        const mesesAtras = parseInt(periodo);
        const dataLimite = subMonths(new Date(), mesesAtras);
        return new Date(l.data) >= dataLimite;
      }
      return true;
    };
    const matchCategoria = (l) => filtros.categoria === 'todas' || l.categoria === filtros.categoria;
    const matchTipo = (l) => filtros.tipo === 'todos' || l.tipo === filtros.tipo;
    const matchStatus = () => true;
    const matchValor = (l) => {
      if (filtros.valorMin && (l.valor || 0) < parseFloat(filtros.valorMin)) return false;
      if (filtros.valorMax && (l.valor || 0) > parseFloat(filtros.valorMax)) return false;
      return true;
    };
    return lancamentos.filter(l => aplicaBusca(l) && dentroPeriodo(l) && matchCategoria(l) && matchTipo(l) && matchStatus(l) && matchValor(l));
  }, [lancamentos, filtros, periodo]);

  // Colunas Contabilidade
  const colunasContabilidade = [
    { label: "Data", accessor: (l) => l.data, type: "date" },
    { label: "Tipo", accessor: (l) => l.tipo === 'receita' ? 'Receita' : 'Despesa' },
    { label: "Categoria", accessor: (l) => l.categoria },
    { label: "Descrição", accessor: (l) => l.descricao },
    { label: "Valor", accessor: (l) => l.valor, type: "currency" },
    { label: "Banco", accessor: (l) => l.conta_banco || '' },
    { label: "Conta", accessor: (l) => l.conta_numero || '' },
    { label: "Origem", accessor: (l) => l.origem_tipo },
    { label: "Origem ID", accessor: (l) => l.origem_id },
    { label: "Fornecedor/Cliente", accessor: (l) => l.fornecedor_cliente || '' },
    { label: "Safra", accessor: (l) => l.safra_id || '' }
  ];

  // Valor estoque insumos
  const valorEstoque = insumos.reduce((acc, i) =>
    acc + (i.quantidade_estoque || 0) * (i.custo_unitario || 0), 0
  );

  // Valor estoque grãos (Conversão KG -> SCS • Usando saldo real da View)
  const totalSacasRelatorio = estoquesGrao.reduce((acc, e) => acc + (Number(e.quantidade_real) || 0), 0) / 60;
  const valorEstoqueGraos = totalSacasRelatorio * 120; // Valor médio de mercado estimado


  // Saldo devedor financiamentos
  const saldoDevedorTotal = financiamentos
    .filter(f => f.status === 'ativo')
    .reduce((acc, f) => acc + (f.saldo_devedor || f.valor_total || 0), 0);

  const handlePrint = () => {
    window.print();
  };

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  // Consolidated Loading State
  const isLoading = loadingDre || loadingFluxo;

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <FileText className="w-8 h-8 text-indigo-600" />
            Relatórios
          </h1>
          <p className="text-stone-600 mt-1">Relatórios prontos para bancos e análise</p>
        </div>

        <div className="flex gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Últimos 3 meses</SelectItem>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="24">Últimos 24 meses</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={handlePrint} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
            <Printer className="w-4 h-4" />
            Imprimir
          </Button>
        </div>
      </div>

      <FiltrosAvancados
        filtros={filtros}
        onFiltrosChange={setFiltros}
        categorias={categoriasTodas}
        tipos={[{ value: 'receita', label: 'Receitas' }, { value: 'despesa', label: 'Despesas' }]}
        statusOptions={[
          { value: 'pago', label: 'Pago/Recebido' },
          { value: 'pendente', label: 'Pendente' },
          { value: 'atrasado', label: 'Atrasado' }
        ]}
        showValorRange={true}
      />

      <Tabs value={tipoRelatorio} onValueChange={setTipoRelatorio}>
        <TabsList className="bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="dre" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            DRE Rural
          </TabsTrigger>
          <TabsTrigger value="fluxo" className="gap-2">
            <Wallet className="w-4 h-4" />
            Fluxo de Caixa
          </TabsTrigger>
          <TabsTrigger value="fluxo_detalhado" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Fluxo Detalhado
          </TabsTrigger>
          <TabsTrigger value="rentabilidade" className="gap-2">
            <Wheat className="w-4 h-4" />
            Rentabilidade
          </TabsTrigger>
          <TabsTrigger value="ativos" className="gap-2">
            <Truck className="w-4 h-4" />
            Desempenho Ativos
          </TabsTrigger>
          <TabsTrigger value="viabilidade" className="gap-2">
            <Building2 className="w-4 h-4" />
            Viabilidade
          </TabsTrigger>
          <TabsTrigger value="contas" className="gap-2">
            <FileText className="w-4 h-4" />
            Relatórios de Contas
          </TabsTrigger>
          <TabsTrigger value="bancario" className="gap-2">
            <Landmark className="w-4 h-4" />
            Bancário
          </TabsTrigger>
          <TabsTrigger value="contabilidade" className="gap-2">
            <FileCheck className="w-4 h-4" />
            Exportação Contábil
          </TabsTrigger>

        </TabsList>

        <TabsContent value="dre" className="mt-6">
          <div ref={reportRef} className="bg-white rounded-2xl border border-stone-200 overflow-hidden print:shadow-none">
            <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-[#1B4332] to-[#2D6A4F] text-white print:bg-[#1B4332]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Demonstrativo de Resultado</h2>
                  <p className="text-white/80 mt-1">{propriedade?.nome}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/60">Período</p>
                  <p className="font-medium">Últimos {periodo} meses</p>
                  <p className="text-sm text-white/60 mt-1">
                    Gerado em {format(new Date(), "dd/MM/yyyy")}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-bold text-lg text-green-700 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  RECEITAS OPERACIONAIS
                </h3>
                <div className="space-y-2">
                  {dreData?.receitas?.map(([cat, valor]) => (
                    <div key={cat} className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">{cat}</span>
                      <span className="font-medium text-green-600">{formatCurrency(valor)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-3 bg-green-50 px-4 rounded-lg font-bold">
                    <span className="text-green-800">TOTAL RECEITAS</span>
                    <span className="text-green-700">{formatCurrency(dreData?.totalReceitas)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-lg text-red-700 mb-3 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  DESPESAS OPERACIONAIS
                </h3>
                <div className="space-y-2">
                  {dreData?.despesas?.map(([cat, valor]) => (
                    <div key={cat} className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">{cat}</span>
                      <span className="font-medium text-red-600">({formatCurrency(valor)})</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-3 bg-red-50 px-4 rounded-lg font-bold">
                    <span className="text-red-800">TOTAL DESPESAS</span>
                    <span className="text-red-700">({formatCurrency(dreData?.totalDespesas)})</span>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-xl ${dreData?.resultado >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold text-stone-800">RESULTADO DO PERÍODO</span>
                  <span className={`text-3xl font-bold ${dreData?.resultado >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(dreData?.resultado)}
                  </span>
                </div>
                <p className="text-stone-600 mt-2">
                  Margem: {((dreData?.resultado / (dreData?.totalReceitas || 1)) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fluxo" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Fluxo de Caixa</h2>
                  <p className="text-white/80 mt-1">{propriedade?.nome}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/60">Período</p>
                  <p className="font-medium">Últimos {periodo} meses</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-stone-200">
                      <th className="text-left py-3 px-4 font-bold text-stone-700">Mês</th>
                      <th className="text-right py-3 px-4 font-bold text-green-700">Entradas</th>
                      <th className="text-right py-3 px-4 font-bold text-red-700">Saídas</th>
                      <th className="text-right py-3 px-4 font-bold text-stone-700">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fluxoCaixaData.map((mes) => {
                      return (
                        <tr key={mes.mes} className="border-b border-stone-100 hover:bg-stone-50">
                          <td className="py-3 px-4 font-medium">{mes.mes}</td>
                          <td className="py-3 px-4 text-right text-green-600 font-medium">
                            {formatCurrency(mes.receitas)}
                          </td>
                          <td className="py-3 px-4 text-right text-red-600 font-medium">
                            ({formatCurrency(mes.despesas)})
                          </td>
                          <td className={`py-3 px-4 text-right font-bold ${mes.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(mes.saldo)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-stone-100 font-bold">
                      <td className="py-4 px-4">TOTAL</td>
                      <td className="py-4 px-4 text-right text-green-700">
                        {formatCurrency(fluxoCaixaData.reduce((a, m) => a + m.receitas, 0))}
                      </td>
                      <td className="py-4 px-4 text-right text-red-700">
                        ({formatCurrency(fluxoCaixaData.reduce((a, m) => a + m.despesas, 0))})
                      </td>
                      <td className={`py-4 px-4 text-right ${fluxoCaixaData.reduce((a, m) => a + m.saldo, 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(fluxoCaixaData.reduce((a, m) => a + m.saldo, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="viabilidade" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-[#D4A373] to-[#BC8A5F] text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Relatório de Viabilidade Econômico-Financeira</h2>
                  <p className="text-white/80 mt-1">Para apresentação a instituições financeiras</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-xl">{propriedade?.nome}</p>
                  <p className="text-sm text-white/60 mt-1">
                    {format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              <section>
                <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b border-stone-200">
                  1. DADOS DA PROPRIEDADE
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-sm text-stone-500">Nome</p>
                    <p className="font-semibold text-lg">{propriedade?.nome}</p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-sm text-stone-500">Localização</p>
                    <p className="font-semibold text-lg">{propriedade?.localizacao || '-'}</p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-sm text-stone-500">Área Total</p>
                    <p className="font-semibold text-lg">{propriedade?.area_total ? `${propriedade.area_total} hectares` : '-'}</p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <p className="text-sm text-stone-500">CAR</p>
                    <p className="font-semibold text-lg">{propriedade?.car || '-'}</p>
                  </div>
                </div>
              </section>

              <section>
                <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b border-stone-200">
                  2. RESUMO FINANCEIRO (Últimos {periodo} meses)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-green-600">Receita Total</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(dreData?.totalReceitas)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-red-600">Despesa Total</p>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(dreData?.totalDespesas)}</p>
                    </CardContent>
                  </Card>
                  <Card className={`${dreData?.resultado >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-stone-50 border-stone-200'}`}>
                    <CardContent className="p-4">
                      <p className="text-sm text-stone-600">Resultado Líquido</p>
                      <p className={`text-2xl font-bold ${dreData?.resultado >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        {formatCurrency(dreData?.resultado)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </section>

              <section>
                <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b border-stone-200">
                  3. HISTÓRICO DE PRODUÇÃO
                </h3>
                {safras.length === 0 ? (
                  <p className="text-stone-500">Nenhuma safra cadastrada</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-3 font-semibold">Safra</th>
                          <th className="text-left py-2 px-3 font-semibold">Cultura</th>
                          <th className="text-right py-2 px-3 font-semibold">Área (ha)</th>
                          <th className="text-right py-2 px-3 font-semibold">Produção</th>
                          <th className="text-center py-2 px-3 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safras.map(s => {
                          const producaoSacas = (Number(s.producao_real) || 0) / 60;
                          return (
                            <tr key={s.id} className="border-b border-stone-100">
                              <td className="py-2 px-3 font-medium">{s.nome}</td>
                              <td className="py-2 px-3">{s.cultura}</td>
                              <td className="py-2 px-3 text-right">{s.area_plantada || '-'}</td>
                              <td className="py-2 px-3 text-right">
                                {producaoSacas > 0 ? `${producaoSacas.toLocaleString('pt-BR')} sc` : (s.producao_esperada ? `${s.producao_esperada} sc` : '-')}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <Badge variant="secondary">{s.status}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section>
                <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b border-stone-200">
                  4. PATRIMÔNIO E ESTOQUE
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Package className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="text-sm text-stone-500">Estoque de Insumos</p>
                          <p className="text-xl font-bold">{formatCurrency(valorEstoque)}</p>
                          <p className="text-xs text-stone-400">{insumos.length} itens</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Wheat className="w-8 h-8 text-amber-600" />
                        <div>
                          <p className="text-sm text-stone-500">Estoque de Grãos</p>
                          <p className="text-xl font-bold">{formatCurrency(valorEstoqueGraos)}</p>
                          <p className="text-xs text-stone-400">{totalSacasRelatorio.toLocaleString('pt-BR')} sacas</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Landmark className="w-8 h-8 text-red-600" />
                        <div>
                          <p className="text-sm text-red-600">Saldo Devedor</p>
                          <p className="text-xl font-bold text-red-700">{formatCurrency(saldoDevedorTotal)}</p>
                          <p className="text-xs text-red-500">{financiamentos.filter(f => f.status === 'ativo').length} financiamentos ativos</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </section>

              <section className={`p-6 rounded-xl ${dreData?.resultado >= 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <h3 className="font-bold text-lg text-stone-800 mb-2">5. ANÁLISE DE VIABILIDADE</h3>
                <p className="text-stone-700">
                  {dreData?.resultado >= 0
                    ? `A propriedade ${propriedade?.nome} apresenta resultado operacional positivo de ${formatCurrency(dreData?.resultado)} no período analisado, demonstrando capacidade de geração de caixa e viabilidade econômico-financeira para honrar compromissos financeiros.`
                    : `A propriedade ${propriedade?.nome} apresenta resultado operacional de ${formatCurrency(dreData?.resultado)} no período analisado. Recomenda-se análise detalhada das condições de financiamento.`
                  }
                </p>
                <div className="mt-4 pt-4 border-t border-stone-300 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">Margem Operacional</p>
                    <p className="font-bold text-stone-800">{((dreData?.resultado / (dreData?.totalReceitas || 1)) * 100).toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Receita Mensal Média</p>
                    <p className="font-bold text-stone-800">{formatCurrency(dreData?.totalReceitas / parseInt(periodo))}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Patrimônio em Estoque</p>
                    <p className="font-bold text-green-700">{formatCurrency(valorEstoque + valorEstoqueGraos)}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Endividamento</p>
                    <p className="font-bold text-red-700">{formatCurrency(saldoDevedorTotal)}</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fluxo_detalhado" className="mt-6">
          <RelatorioFluxoCaixaDetalhado
            lancamentos={lancamentosFiltrados}
            contasPagar={contasPagar}
            contasReceber={contasReceber}
            propriedade={propriedade}
            filtros={filtros}
          />
        </TabsContent>

        <TabsContent value="contas" className="mt-6">
          <RelatorioContas
            contasPagar={contasPagar}
            contasReceber={contasReceber}
            propriedade={propriedade}
            filtros={filtros}
          />
        </TabsContent>

        <TabsContent value="rentabilidade" className="mt-6">
          <RelatorioRentabilidadeSafra
            safras={safras}
            lancamentos={lancamentosFiltrados}
            custosAlocados={custosAlocados.filter(c => {
              if (filtros.dataInicio && c.data < filtros.dataInicio) return false;
              if (filtros.dataFim && c.data > filtros.dataFim) return false;
              if (filtros.categoria !== 'todas' && c.categoria !== filtros.categoria) return false;
              return true;
            })}
            propriedade={propriedade}
            filtros={filtros}
          />
        </TabsContent>

        <TabsContent value="ativos" className="mt-6">
          <RelatorioDesempenhoAtivos
            ativos={ativos}
            manutencoes={manutencoes}
            propriedade={propriedade}
            filtros={filtros}
          />
        </TabsContent>

        <TabsContent value="bancario" className="mt-6">
          <RelatorioBancario
            propriedade={propriedade}
            lancamentos={lancamentosFiltrados}
            safras={safras}
            insumos={insumos}
            periodo={periodo}
            dreData={dreData}
            fluxoCaixaData={fluxoCaixaData}
            financiamentos={financiamentos}
            estoquesGrao={estoquesGrao}
            filtros={filtros}
          />
        </TabsContent>

        <TabsContent value="contabilidade" className="mt-6">
          <Card className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-stone-600 to-stone-700 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Exportação Contábil</h2>
                  <p className="text-white/80 mt-1">vw_lancamentos_contabilidade • {propriedade?.nome}</p>
                </div>
                <ExportacaoRelatorios
                  dados={lancContabilidade}
                  nomeArquivo="Exportacao_Contabilidade"
                  colunas={colunasContabilidade}
                />
              </div>
            </div>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lancContabilidade.slice(0, 10).map(l => (
                  <div key={l.id} className="p-3 rounded-lg border border-stone-200">
                    <div className="flex justify-between text-sm">
                      <span>{new Date(l.data).toLocaleDateString('pt-BR')}</span>
                      <span className={`font-medium ${l.tipo === 'receita' ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(l.valor)}</span>
                    </div>
                    <div className="text-xs text-stone-600 mt-1">
                      {l.categoria} • {l.descricao}
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                      {l.conta_banco || ''} {l.conta_numero ? `• ${l.conta_numero}` : ''}
                    </div>
                  </div>
                ))}
                {lancContabilidade.length === 0 && (
                  <div className="text-sm text-stone-600">Nenhum lançamento disponível</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>


      </Tabs>
    </div>
  );
}

function RelatorioContas({ contasPagar = [], contasReceber = [], propriedade, filtros }) {
  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const filtrar = useCallback((c, tipo) => {
    if (filtros.busca) {
      const b = filtros.busca.toLowerCase();
      const texto = tipo === 'pagar' ? `${c.descricao || ''} ${c.fornecedor || ''} ${c.categoria || ''}` : `${c.descricao || ''} ${c.cliente || ''} ${c.categoria || ''}`;
      if (!texto.toLowerCase().includes(b)) return false;
    }
    if (filtros.dataInicio && (c.data_vencimento || c.data_pagamento || c.data_recebimento) < filtros.dataInicio) return false;
    if (filtros.dataFim && (c.data_vencimento || c.data_pagamento || c.data_recebimento) > filtros.dataFim) return false;
    if (filtros.categoria && filtros.categoria !== 'todas' && c.categoria !== filtros.categoria) return false;
    if (filtros.status && filtros.status !== 'todos') {
      const s = c.status;
      if (s !== filtros.status) return false;
    }
    if (filtros.valorMin && (c.valor || 0) < parseFloat(filtros.valorMin)) return false;
    if (filtros.valorMax && (c.valor || 0) > parseFloat(filtros.valorMax)) return false;
    return true;
  }, [filtros]);
  const pagarFiltradas = useMemo(() => contasPagar.filter(c => filtrar(c, 'pagar')), [contasPagar, filtrar]);
  const receberFiltradas = useMemo(() => contasReceber.filter(c => filtrar(c, 'receber')), [contasReceber, filtrar]);
  const totalPagarPend = useMemo(() => pagarFiltradas.filter(c => c.status !== 'pago').reduce((a, c) => a + (c.valor || 0), 0), [pagarFiltradas]);
  const totalPagarPago = useMemo(() => pagarFiltradas.filter(c => c.status === 'pago').reduce((a, c) => a + ((c.valor || 0) + (c.juros || 0) + (c.multa || 0) - (c.desconto || 0)), 0), [pagarFiltradas]);
  const totalReceberPend = useMemo(() => receberFiltradas.filter(c => c.status !== 'recebido').reduce((a, c) => a + (c.valor || 0), 0), [receberFiltradas]);
  const totalReceberRec = useMemo(() => receberFiltradas.filter(c => c.status === 'recebido').reduce((a, c) => a + ((c.valor || 0) + (c.juros || 0) + (c.multa || 0) - (c.desconto || 0)), 0), [receberFiltradas]);
  const saldo = useMemo(() => totalReceberRec - totalPagarPago, [totalReceberRec, totalPagarPago]);
  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-stone-600 to-stone-700 text-white print:bg-stone-600">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Relatórios de Contas</h2>
            <p className="text-white/80 mt-1">{propriedade?.nome}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-white/60">Período</p>
            <p className="font-medium">{filtros.dataInicio && filtros.dataFim ? `${format(new Date(filtros.dataInicio), 'dd/MM/yyyy')} - ${format(new Date(filtros.dataFim), 'dd/MM/yyyy')}` : `Últimos meses`}</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-red-50 border-red-200"><CardContent className="p-4 text-center"><p className="text-sm text-red-600">A Pagar (pendente)</p><p className="text-2xl font-bold text-red-700 mt-1">{formatCurrency(totalPagarPend)}</p></CardContent></Card>
          <Card className="bg-red-50 border-red-200"><CardContent className="p-4 text-center"><p className="text-sm text-red-600">Pagas</p><p className="text-2xl font-bold text-red-700 mt-1">{formatCurrency(totalPagarPago)}</p></CardContent></Card>
          <Card className="bg-green-50 border-green-200"><CardContent className="p-4 text-center"><p className="text-sm text-green-600">A Receber (pendente)</p><p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(totalReceberPend)}</p></CardContent></Card>
          <Card className="bg-green-50 border-green-200"><CardContent className="p-4 text-center"><p className="text-sm text-green-600">Recebidas</p><p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(totalReceberRec)}</p></CardContent></Card>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-stone-200">
                <th className="text-left py-3 px-4 font-semibold">Tipo</th>
                <th className="text-left py-3 px-4 font-semibold">Descrição</th>
                <th className="text-left py-3 px-4 font-semibold">Categoria</th>
                <th className="text-left py-3 px-4 font-semibold">Pessoa</th>
                <th className="text-center py-3 px-4 font-semibold">Status</th>
                <th className="text-center py-3 px-4 font-semibold">Vencimento</th>
                <th className="text-right py-3 px-4 font-semibold">Valor</th>
                <th className="text-right py-3 px-4 font-semibold">Total Pago/Recebido</th>
              </tr>
            </thead>
            <tbody>
              {[...pagarFiltradas.map(c => ({ ...c, __tipo: 'Pagar' })), ...receberFiltradas.map(c => ({ ...c, __tipo: 'Receber' }))]
                .sort((a, b) => new Date((a.data_vencimento || a.data_pagamento || a.data_recebimento)) - new Date((b.data_vencimento || b.data_pagamento || b.data_recebimento)))
                .map(c => (
                  <tr key={`${c.__tipo}-${c.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-2 px-4 font-medium">{c.__tipo}</td>
                    <td className="py-2 px-4">{c.descricao}</td>
                    <td className="py-2 px-4">{c.categoria}</td>
                    <td className="py-2 px-4">{c.__tipo === 'Pagar' ? (c.fornecedor || '') : (c.cliente || '')}</td>
                    <td className="py-2 px-4 text-center"><Badge variant="secondary">{c.status}</Badge></td>
                    <td className="py-2 px-4 text-center">{new Date(c.data_vencimento || c.data_pagamento || c.data_recebimento).toLocaleDateString('pt-BR')}</td>
                    <td className={`py-2 px-4 text-right ${c.__tipo === 'Pagar' ? 'text-red-700' : 'text-green-700'}`}>{formatCurrency(c.valor || 0)}</td>
                    <td className={`py-2 px-4 text-right font-semibold ${c.__tipo === 'Pagar' ? 'text-red-700' : 'text-green-700'}`}>{c.status === (c.__tipo === 'Pagar' ? 'pago' : 'recebido') ? formatCurrency((c.valor || 0) + (c.juros || 0) + (c.multa || 0) - (c.desconto || 0)) : '-'}</td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr className="bg-stone-100 font-bold">
                <td className="py-4 px-4" colSpan={6}>SALDO (Recebido - Pago)</td>
                <td className="py-4 px-4 text-right" colSpan={2}>
                  <span className={saldo >= 0 ? "text-green-700" : "text-red-700"}>{formatCurrency(saldo)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
